import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import Alert from './Alert';
import apiService from '../../services/ApiService';
import ColegioService from '../../services/ColegioService';
import brandLogo from '../../assets/logos/logo_transparent_background.png';
import './ChatModal.css';

const getAssistantDisplayName = (assistant, headerTitle) =>
  headerTitle ||
  assistant?.nombreAsistente ||
  assistant?.nombre ||
  assistant?.Nombre ||
  assistant?.displayName ||
  assistant?.DisplayName ||
  assistant?.email ||
  assistant?.Email ||
  'Asistente';

const isClassicAssistant = (assistant) => Boolean(
  assistant?.esClasico ||
  assistant?.EsClasico ||
  assistant?.gptClasicoModelo ||
  assistant?.GPTClasicoModelo ||
  assistant?.gptClasicoNombre ||
  assistant?.GPTClasicoNombre ||
  assistant?.gptClasicoCreacionFinalizada ||
  assistant?.GPTClasicoCreacionFinalizada
);

const getOpenAIChatUiVariant = () =>
  String(process.env.REACT_APP_OPENAI_CHAT_MODAL_UI || 'legacy').toLowerCase().trim();

const ChatModal = ({ isOpen, onClose, assistant, publicChat = false, colegioId, password, headerTitle }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showAudioToggle, setShowAudioToggle] = useState(false);
  const [audioReplyEnabled, setAudioReplyEnabled] = useState(false);
  const [modernMessagesHeight, setModernMessagesHeight] = useState(null);
  const messagesEndRef = useRef(null);
  const modernChatRef = useRef(null);
  const modernTopbarRef = useRef(null);
  const modernBodyRef = useRef(null);
  const modernAlertRef = useRef(null);
  const modernComposerRef = useRef(null);

  const assistantName = useMemo(
    () => getAssistantDisplayName(assistant, headerTitle),
    [assistant, headerTitle]
  );
  const assistantArea = assistant?.nombreArea || assistant?.NombreArea || '';
  const assistantMateria = assistant?.nombreMateria || assistant?.NombreMateria || '';
  const assistantDescription = assistant?.descripcion || assistant?.Descripcion || '';
  const chatUiVariant = useMemo(() => getOpenAIChatUiVariant(), []);
  const useModernOpenAIUI = !isClassicAssistant(assistant) && chatUiVariant === 'modern';
  const systemMessages = useMemo(
    () => messages.filter((message) => message.role === 'system'),
    [messages]
  );
  const conversationMessages = useMemo(
    () => messages.filter((message) => message.role !== 'system'),
    [messages]
  );
  const quickPrompts = useMemo(() => {
    const context = assistantMateria || assistantArea || assistantName;
    return [
      `Explicame ${context} de forma simple y clara.`,
      `Dame 3 ejemplos practicos sobre ${context}.`,
      `Haceme 5 preguntas para evaluar comprension sobre ${context}.`
    ];
  }, [assistantArea, assistantMateria, assistantName]);
  const modernBrandSubtitle = publicChat ? 'Asistente OpenAI del aula' : 'OpenAI assistant preview';
  const modernModeLabel = publicChat ? 'Chat del aula' : 'Modo preview';
  const modernAssistantDescription = assistantDescription || (
    publicChat
      ? 'Asistente OpenAI disponible para conversar desde el aula.'
      : 'Asistente OpenAI listo para una prueba conversacional.'
  );
  const modernTopbarDescription = isInitializing
    ? 'Preparando la conversación...'
    : threadId
      ? (publicChat
        ? 'Conversación iniciada usando los endpoints públicos del aula'
        : 'Usando los mismos endpoints del chat actual')
      : 'Esperando conexión';
  const modernLoaderDescription = publicChat
    ? 'Estamos creando el thread público para comenzar la conversación.'
    : 'Estamos creando el thread de prueba para comenzar la conversación.';
  const modernHeroDescription = publicChat
    ? 'Inicia una conversación con una interfaz moderna estilo ChatGPT/Chainlit, manteniendo la lógica y los endpoints públicos del aula.'
    : 'Ejecuta una prueba con una interfaz moderna estilo ChatGPT/Chainlit, manteniendo exactamente la misma lógica y los mismos endpoints del chat actual.';
  const modernInputPlaceholder = isInitializing
    ? 'Inicializando...'
    : (publicChat
      ? 'Escribe tu mensaje para conversar con el asistente...'
      : 'Escribe tu mensaje para probar el asistente...');
  const modernFooterNote = publicChat
    ? 'Chat público de asistentes OpenAI con UI moderna aluxIA.'
    : 'Vista de prueba de asistentes OpenAI con UI moderna aluxIA.';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !useModernOpenAIUI) {
      setModernMessagesHeight(null);
      return undefined;
    }

    let frameId = 0;

    const updateMessagesViewport = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const chatEl = modernChatRef.current;
        const topbarEl = modernTopbarRef.current;
        const composerEl = modernComposerRef.current;

        if (!chatEl || !topbarEl || !composerEl) {
          return;
        }

        const alertEl = modernAlertRef.current;
        const nextHeight = Math.max(
          320,
          Math.floor(
            chatEl.clientHeight
            - topbarEl.offsetHeight
            - composerEl.offsetHeight
            - (alertEl?.offsetHeight || 0)
          )
        );

        setModernMessagesHeight((previousHeight) => (
          previousHeight === nextHeight ? previousHeight : nextHeight
        ));
      });
    };

    updateMessagesViewport();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          updateMessagesViewport();
        })
      : null;

    if (resizeObserver) {
      [modernChatRef.current, modernTopbarRef.current, modernComposerRef.current, modernAlertRef.current]
        .filter(Boolean)
        .forEach((element) => resizeObserver.observe(element));
    }

    window.addEventListener('resize', updateMessagesViewport);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMessagesViewport);
    };
  }, [isOpen, useModernOpenAIUI, alert.show, showAudioToggle]);

  useEffect(() => {
    if (isOpen && assistant) {
      const assistantFlags = assistant?.featureFlags || assistant?.FeatureFlags || [];
      const flags = assistantFlags.length > 0 ? assistantFlags : ColegioService.getFeatureFlags();
      const audioFlag = flags.find((flag) => {
        const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
        return nombre === 'permiterespuestaaudiochat';
      });
      const isAudioEnabled = Boolean(audioFlag?.activo ?? audioFlag?.Activo);
      setShowAudioToggle(isAudioEnabled);
      if (!isAudioEnabled) {
        setAudioReplyEnabled(false);
      }
      initializeChat();
    } else {
      setMessages([]);
      setThreadId(null);
      setIsInitializing(true);
      setInputMessage('');
      setAlert({ show: false, message: '', type: 'error' });
      setShowAudioToggle(false);
      setAudioReplyEnabled(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assistant]);

  const initializeChat = async () => {
    try {
      setIsInitializing(true);
      setAlert({ show: false, message: '', type: 'error' });

      const assistantId = assistant?.idOpenAIAssistant || assistant?.idAssistant || assistant?.id;
      if (!assistantId) {
        throw new Error('No se encontró el asistente seleccionado');
      }

      if (publicChat && !colegioId) {
        throw new Error('Falta el colegio para iniciar la conversación');
      }

      if (publicChat) {
        const response = await apiService.postPublic('/Asistente/publico/threads', {
          idOpenAIAssistant: assistantId,
          colegioId,
          password
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      } else {
        const response = await apiService.post('/Threads/threads', {
          idOpenAIAssistant: assistantId
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      }

      setMessages([
        {
          id: `welcome-${Date.now()}`,
          content: `Conversación iniciada con el asistente ${assistantName}`,
          role: 'system',
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      console.error('❌ Error al inicializar chat:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al inicializar la conversación',
        type: 'error'
      });
    } finally {
      setIsInitializing(false);
    }
  };

  const extractHtmlFromMarkdown = (content) => {
    const htmlBlockMatch = content.match(/```html\s*\n([\s\S]*?)\n```/);
    if (htmlBlockMatch) {
      const htmlContent = htmlBlockMatch[1];
      return extractBodyContent(htmlContent);
    }

    const genericBlockMatch = content.match(/```\s*\n([\s\S]*?)\n```/);
    if (genericBlockMatch) {
      const innerContent = genericBlockMatch[1];
      if (innerContent.includes('<html>') || innerContent.includes('<!DOCTYPE')) {
        return extractBodyContent(innerContent);
      }
    }

    if (content.includes('<!DOCTYPE') || content.includes('<html>')) {
      return extractBodyContent(content);
    }

    return content;
  };

  const extractBodyContent = (htmlString) => {
    const bodyMatch = htmlString.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      return bodyMatch[1].trim();
    }

    const afterHeadMatch = htmlString.match(/<\/head>\s*([\s\S]*?)<\/html>/i);
    if (afterHeadMatch) {
      return afterHeadMatch[1].trim();
    }

    return htmlString;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !threadId || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      content: inputMessage,
      role: 'user',
      timestamp: new Date()
    };

    const messageToSend = inputMessage.trim();
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const assistantId = assistant?.idOpenAIAssistant || assistant?.idAssistant || assistant?.id;
      if (!assistantId) {
        throw new Error('No se encontró el asistente seleccionado');
      }

      if (publicChat && !colegioId) {
        throw new Error('Falta el colegio para enviar el mensaje');
      }

      const response = publicChat
        ? await apiService.postPublic('/Asistente/publico/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            colegioId,
            password,
            permiteRespuestaAudioChat: audioReplyEnabled
          })
        : await apiService.post('/Asistente/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            permiteRespuestaAudioChat: audioReplyEnabled
          });

      if (response.ok && response.respuesta) {
        const htmlContent = extractHtmlFromMarkdown(response.respuesta);
        const archivoIds = response.archivoIds || response.ArchivoIds || [];
        const archivoLinks = response.archivoLinks || response.ArchivoLinks || [];
        const hasServerLinks = Array.isArray(archivoLinks) && archivoLinks.length > 0;
        const shouldStripLinks = hasServerLinks || /\/mnt\/data\//i.test(htmlContent);
        const safeHtmlContent = shouldStripLinks
          ? htmlContent.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
          : htmlContent;
        const downloadLinks = (Array.isArray(archivoLinks) && archivoLinks.length > 0)
          ? `
            <div class="download-box">
              <p><strong>Archivos generados</strong></p>
              <ul>
                ${archivoLinks.map((href, idx) => `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`).join('')}
              </ul>
            </div>
          `
          : (Array.isArray(archivoIds) && archivoIds.length > 0
            ? `
              <div class="download-box">
                <p><strong>Archivos generados</strong></p>
                <ul>
                  ${archivoIds.map((fileId, idx) => {
                    const href = publicChat
                      ? `${apiService.baseURL}/Asistente/publico/archivos/${fileId}/download?colegioId=${colegioId}`
                      : `${apiService.baseURL}/Asistente/archivos/${fileId}/download`;
                    return `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`;
                  }).join('')}
                </ul>
              </div>
            `
            : '');
        const audioBase64 = response.audioBase64 || response.AudioBase64;
        const audioContentType = response.audioContentType || response.AudioContentType || 'audio/mpeg';
        const audioSrc = audioBase64 ? `data:${audioContentType};base64,${audioBase64}` : '';
        const audioBlock = audioSrc
          ? `
            <div class="download-box audio-box">
              <p><strong>Respuesta en audio</strong></p>
              <audio controls src="${audioSrc}"></audio>
              <a href="${audioSrc}" download="respuesta.mp3">Descargar audio</a>
            </div>
          `
          : '';
        const finalContent = `${safeHtmlContent}${downloadLinks}${audioBlock}`;

        const assistantMessage = {
          id: response.idMensaje || `assistant-${Date.now()}`,
          content: finalContent,
          role: 'assistant',
          timestamp: new Date(response.fechaRespuesta || new Date())
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        throw new Error(response.error || 'Error al procesar la respuesta del asistente');
      }
    } catch (error) {
      console.error('❌ Error al enviar mensaje:', error);

      const errorMessage = {
        id: `error-${Date.now()}`,
        content: `<div class="error-message">
          <p><strong>Error al enviar mensaje</strong></p>
          <p>Lo siento, no pude procesar tu mensaje. Por favor, inténtalo nuevamente.</p>
          <p><em>Detalles: ${error.message}</em></p>
        </div>`,
        role: 'assistant',
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, errorMessage]);
      setAlert({
        show: true,
        message: `Error al enviar mensaje: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sanitizeHtml = (html) => {
    // eslint-disable-next-line no-unused-vars
    const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote',
      'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'hr', 'mark', 'div'];
    return html;
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  const getMessageMarkup = (message) => ({
    __html: message.role === 'assistant'
      ? sanitizeHtml(message.content)
      : String(message.content || '').replace(/\n/g, '<br>')
  });

  const applyQuickPrompt = (prompt) => {
    setInputMessage(prompt);
  };

  const renderLegacyChat = () => (
    <div className="chat-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`chat-modal${publicChat ? ' public-chat' : ''}`}>
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="assistant-avatar">
              <i className="fas fa-robot"></i>
            </div>
            <div className="assistant-info">
              <h3 className="assistant-name">{assistantName || 'Asistente IA'}</h3>
              <p className="assistant-status">
                {isInitializing ? 'Inicializando...' : threadId ? 'Conectado' : 'Sin conexión'}
              </p>
            </div>
          </div>

          <button className="close-btn" onClick={onClose} type="button">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {alert.show && (
          <div className="chat-alert">
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          </div>
        )}

        <div className="chat-messages">
          {isInitializing ? (
            <div className="initializing-message">
              <div className="thinking-content">
                <div className="thinking-avatar">
                  <i className="fas fa-cog fa-spin"></i>
                </div>
                <div className="thinking-text">
                  <span className="thinking-label">
                    Inicializando conversación con {assistantName}...
                  </span>
                  <div className="typing-indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.role === 'user' ? 'user-message' : message.role === 'system' ? 'system-message' : 'assistant-message'}`}
                >
                  <div className="message-content">
                    <div
                      className="message-text"
                      dangerouslySetInnerHTML={getMessageMarkup(message)}
                    />
                    {message.timestamp && (
                      <div className="message-time">
                        {formatTime(message.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="message assistant-message thinking-message">
                  <div className="message-content">
                    <div className="message-text thinking-bubble">
                      <div className="thinking-content">
                        <div className="thinking-avatar">
                          <i className="fas fa-robot"></i>
                        </div>
                        <div className="thinking-text">
                          <span className="thinking-label">
                            {assistantName} está escribiendo
                          </span>
                          <div className="typing-indicator">
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="chat-input-area">
          {showAudioToggle && (
            <div className="chat-options">
              <label className="audio-toggle">
                <input
                  type="checkbox"
                  checked={audioReplyEnabled}
                  onChange={(e) => setAudioReplyEnabled(e.target.checked)}
                />
                <span>Respuesta en forma de audio</span>
              </label>
            </div>
          )}
          <div className="chat-input-container">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isInitializing ? 'Inicializando...' : 'Escribe tu mensaje...'}
              disabled={isLoading || isInitializing || !threadId}
              rows={1}
              className="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || isInitializing || !threadId}
              className="send-btn"
              type="button"
            >
              {isLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderModernChat = () => (
    <div className="chat-modal-overlay modern-chat-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="chat-modal modern-openai-chat" ref={modernChatRef}>
        <div className="modern-openai-shell">
          <aside className="modern-openai-sidebar">
            <div className="modern-openai-brand">
              <img src={brandLogo} alt="aluxIA" className="modern-openai-brand-logo" />
              <div className="modern-openai-brand-copy">
                <span className="modern-openai-brand-name">aluxIA</span>
                <span className="modern-openai-brand-subtitle">{modernBrandSubtitle}</span>
              </div>
            </div>

            <div className="modern-openai-assistant-card">
              <div className="modern-openai-assistant-head">
                <div className="modern-openai-avatar">
                  <img src={brandLogo} alt="aluxIA" />
                </div>
                <div>
                  <h2>{assistantName}</h2>
                  <p>{modernAssistantDescription}</p>
                </div>
              </div>

              <div className="modern-openai-status-row">
                <span className={`modern-openai-status-badge ${isInitializing ? 'is-pending' : threadId ? 'is-online' : 'is-offline'}`}>
                  {isInitializing ? 'Inicializando' : threadId ? 'Conectado' : 'Sin conexión'}
                </span>
                {showAudioToggle && (
                  <span className="modern-openai-feature-badge">Audio disponible</span>
                )}
              </div>

              {(assistantArea || assistantMateria) && (
                <div className="modern-openai-meta-grid">
                  {assistantArea && (
                    <div className="modern-openai-meta-item">
                      <span className="modern-openai-meta-label">Area</span>
                      <strong>{assistantArea}</strong>
                    </div>
                  )}
                  {assistantMateria && (
                    <div className="modern-openai-meta-item">
                      <span className="modern-openai-meta-label">Materia</span>
                      <strong>{assistantMateria}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modern-openai-help-card">
              <div className="modern-openai-help-header">
                <i className="fas fa-comments"></i>
                <div>
                  <h3>Sugerencias rápidas</h3>
                  <p>Dispara una conversación con un solo click.</p>
                </div>
              </div>
              <div className="modern-openai-suggestion-list">
                {quickPrompts.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    className="modern-openai-suggestion-chip"
                    onClick={() => applyQuickPrompt(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <section className="modern-openai-main">
            <header className="modern-openai-topbar" ref={modernTopbarRef}>
              <div className="modern-openai-title-block">
                <span className="modern-openai-mode-pill">{modernModeLabel}</span>
                <div>
                  <h3>{assistantName}</h3>
                  <p>{modernTopbarDescription}</p>
                </div>
              </div>

              <button className="modern-openai-close" onClick={onClose} type="button" aria-label="Cerrar chat">
                <i className="fas fa-times"></i>
              </button>
            </header>

            <div className="modern-openai-body" ref={modernBodyRef}>
              {alert.show && (
                <div className="chat-alert modern-chat-alert" ref={modernAlertRef}>
                  <Alert
                    title={alert.title}
                    message={alert.message}
                    type={alert.type}
                    onClose={closeAlert}
                  />
                </div>
              )}

              <div
                className={`chat-messages modern-openai-messages${conversationMessages.length === 0 && !isLoading ? ' is-empty' : ''}`}
                style={modernMessagesHeight ? { height: `${modernMessagesHeight}px` } : undefined}
              >
                <div className={`modern-openai-messages-stack${conversationMessages.length === 0 && !isLoading ? ' is-empty' : ''}${conversationMessages.length > 0 || isLoading ? ' has-conversation' : ''}`}>
                  {isInitializing ? (
                    <div className="modern-openai-loader-card">
                      <div className="modern-openai-loader-logo">
                        <img src={brandLogo} alt="aluxIA" />
                      </div>
                      <div className="modern-openai-loader-copy">
                        <h4>Inicializando {assistantName}</h4>
                        <p>{modernLoaderDescription}</p>
                        <div className="typing-indicator modern-openai-typing">
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {conversationMessages.length === 0 && (
                        <div className="modern-openai-hero">
                          <div className="modern-openai-hero-logo">
                            <img src={brandLogo} alt="aluxIA" />
                          </div>
                          <span className="modern-openai-hero-badge">Asistente OpenAI</span>
                          <h1>{assistantName}</h1>
                          <p>{modernHeroDescription}</p>
                          {systemMessages[0] && (
                            <div className="modern-openai-system-message">
                              {systemMessages[0].content}
                            </div>
                          )}
                          <div className="modern-openai-hero-actions">
                            {quickPrompts.map((prompt) => (
                              <button
                                key={`hero-${prompt}`}
                                type="button"
                                className="modern-openai-hero-chip"
                                onClick={() => applyQuickPrompt(prompt)}
                              >
                                {prompt}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {conversationMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`modern-openai-message ${message.role === 'user' ? 'is-user' : 'is-assistant'}`}
                        >
                          <div className="modern-openai-message-avatar">
                            {message.role === 'user' ? (
                              <span>TU</span>
                            ) : (
                              <img src={brandLogo} alt="aluxIA" />
                            )}
                          </div>
                          <div className="modern-openai-message-content">
                            <div className="modern-openai-message-author">
                              {message.role === 'user' ? 'Tú' : assistantName}
                            </div>
                            <div
                              className={`message-text modern-openai-message-bubble ${message.role === 'assistant' ? 'modern-openai-assistant-html' : 'modern-openai-user-html'}`}
                              dangerouslySetInnerHTML={getMessageMarkup(message)}
                            />
                            {message.timestamp && (
                              <div className="modern-openai-message-time">
                                {formatTime(message.timestamp)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {isLoading && (
                        <div className="modern-openai-message is-assistant is-thinking">
                          <div className="modern-openai-message-avatar">
                            <img src={brandLogo} alt="aluxIA" />
                          </div>
                          <div className="modern-openai-message-content">
                            <div className="modern-openai-message-author">{assistantName}</div>
                            <div className="modern-openai-thinking-bubble">
                              <span>Generando respuesta</span>
                              <div className="typing-indicator modern-openai-typing">
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {(conversationMessages.length > 0 || isLoading) && <div ref={messagesEndRef} />}
                    </>
                  )}
                </div>
              </div>

              <div className="modern-openai-composer" ref={modernComposerRef}>
                {showAudioToggle && (
                  <label className="modern-openai-audio-toggle">
                    <input
                      type="checkbox"
                      checked={audioReplyEnabled}
                      onChange={(e) => setAudioReplyEnabled(e.target.checked)}
                    />
                    <span>Respuesta en audio</span>
                  </label>
                )}

                <div className="modern-openai-input-shell">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={modernInputPlaceholder}
                    disabled={isLoading || isInitializing || !threadId}
                    rows={1}
                    className="modern-openai-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isLoading || isInitializing || !threadId}
                    className="modern-openai-send"
                    type="button"
                  >
                    {isLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <>
                        <span>Enviar</span>
                        <i className="fas fa-paper-plane"></i>
                      </>
                    )}
                  </button>
                </div>

                <p className="modern-openai-footer-note">
                  {modernFooterNote}
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return useModernOpenAIUI ? renderModernChat() : renderLegacyChat();
};

export default ChatModal;
